import {
  filter,
  find,
  some,
  sortBy,
  startsWith,
  transform,
  without,
} from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { tradeMethodsApi } from 'api';
import { useSciDomain } from 'components';
import { NewStatusDetails, OrderStatus, QueryKey } from 'enums';
import { Order, TradeMethod } from 'types';

type UseTradeMethodSelectionInput = {
  order: Order | undefined;
  authenticated: boolean;
};

export type UseTradeMethodSelectionResult = {
  tradeMethods: TradeMethod[] | undefined;
  shopSciTradeMethods: TradeMethod[] | undefined;
  effectiveTradeMethods: TradeMethod[] | undefined;
  formattedTradeMethods: TradeMethod[];
  selectedTradeMethod: TradeMethod | undefined;
  isTradeMethodsLoading: boolean;
  isShopSciTradeMethodsLoading: boolean;
  isTradeMethodsSuccess: boolean;
  isShopSciTradeMethodsSuccess: boolean;
  shouldLoadTradeMethods: boolean;
  startPaymentSyncFailed: boolean;
  setStartPaymentSyncFailed: (failed: boolean) => void;
};

const TRADE_METHOD_TO_COMBINE = ['sbp', 'tsbp', 'nspk'];
const ANY_BANK_PREFIX = 'any';

export const useTradeMethodSelection = ({
  order,
  authenticated,
}: UseTradeMethodSelectionInput): UseTradeMethodSelectionResult => {
  const { data: sciDomainConfig } = useSciDomain();
  const sciCustomTradeMethodsEnabled =
    !!order?.shop?.sciCustomTradeMethodsEnabled;
  const [selectedTradeMethod, setSelectedTradeMethod] = useState<TradeMethod>();
  const [
    enableSelectedTradeMethodFallback,
    setEnableSelectedTradeMethodFallback,
  ] = useState(false);
  const [startPaymentSyncFailed, setStartPaymentSyncFailed] = useState(false);

  const shouldLoadTradeMethods = useMemo(
    () =>
      authenticated && order?.status === OrderStatus.New && !!sciDomainConfig,
    [authenticated, order?.status, sciDomainConfig],
  );

  const {
    isLoading: isShopSciTradeMethodsLoading,
    isSuccess: isShopSciTradeMethodsSuccess,
    data: shopSciTradeMethods,
  } = useQuery<TradeMethod[]>(
    QueryKey.ShopSciTradeMethods,
    () => tradeMethodsApi.getShopSCITradeMethods((order as Order)?.id),
    {
      enabled: shouldLoadTradeMethods && sciCustomTradeMethodsEnabled,
    },
  );

  const {
    isLoading: isTradeMethodsLoading,
    isSuccess: isTradeMethodsSuccess,
    data: tradeMethods,
  } = useQuery<TradeMethod[]>(
    QueryKey.TradeMethods,
    () => tradeMethodsApi.getAll((order as Order)?.id),
    {
      enabled: shouldLoadTradeMethods && !sciCustomTradeMethodsEnabled,
    },
  );

  const { data: selectedTradeMethodsFallback } = useQuery<TradeMethod>(
    QueryKey.SelectedTradeMethodsFallback,
    () => tradeMethodsApi.getSelectedTradeMethod((order as Order)?.id),
    {
      enabled:
        !!order &&
        !!order.requisites &&
        enableSelectedTradeMethodFallback &&
        authenticated,
      staleTime: Infinity,
    },
  );

  useEffect(() => {
    if (order?.payment?.type && order?.status !== OrderStatus.New) {
      const bank = order?.requisites?.bank || order?.payment?.bank;
      const selectedTradeMethodsLoaded = find(
        tradeMethods,
        (tradeMethod) =>
          tradeMethod.paymentType === order?.payment?.type &&
          (!bank || tradeMethod.bank === bank),
      );
      const tradeMethod =
        selectedTradeMethodsLoaded || selectedTradeMethodsFallback;

      if (tradeMethod) {
        setSelectedTradeMethod(tradeMethod);
      } else if (!enableSelectedTradeMethodFallback) {
        setEnableSelectedTradeMethodFallback(true);
      }
    }
  }, [
    order,
    order?.payment?.type,
    order?.payment?.bank,
    order?.status,
    tradeMethods,
    selectedTradeMethodsFallback,
    enableSelectedTradeMethodFallback,
  ]);

  useEffect(() => {
    if (order?.statusDetails === NewStatusDetails.NewTradeMethodReset) {
      setStartPaymentSyncFailed(false);
      setSelectedTradeMethod(undefined);
      setEnableSelectedTradeMethodFallback(false);
    }
  }, [order?.statusDetails]);

  const effectiveTradeMethods = sciCustomTradeMethodsEnabled
    ? shopSciTradeMethods
    : tradeMethods;

  const formattedTradeMethods = useMemo(() => {
    const methods = effectiveTradeMethods ?? [];
    const sortedMethods = sortBy(methods, ['paymentTypeName', 'bankName']);
    const anyBankMethods = filter(sortedMethods, (m) =>
      startsWith(m.bank, ANY_BANK_PREFIX),
    );
    const transformedMethods = transform<TradeMethod, TradeMethod[]>(
      without(sortedMethods, ...anyBankMethods),
      (acc, tradeMethod) => {
        if (
          !sciCustomTradeMethodsEnabled &&
          TRADE_METHOD_TO_COMBINE.includes(tradeMethod.paymentType)
        ) {
          if (!some(acc, { paymentType: tradeMethod.paymentType })) {
            acc.push({
              ...tradeMethod,
              bankName: null as any as string,
              bank: null as any as string,
            });
          }
        } else {
          acc.push(tradeMethod);
        }
      },
      [],
    );
    return [...anyBankMethods, ...transformedMethods];
  }, [effectiveTradeMethods, sciCustomTradeMethodsEnabled]);

  return {
    tradeMethods,
    shopSciTradeMethods,
    effectiveTradeMethods,
    formattedTradeMethods,
    selectedTradeMethod,
    isTradeMethodsLoading,
    isShopSciTradeMethodsLoading,
    isTradeMethodsSuccess,
    isShopSciTradeMethodsSuccess,
    shouldLoadTradeMethods,
    startPaymentSyncFailed,
    setStartPaymentSyncFailed,
  };
};
